import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { enqueueSnackbar } from '@applift/factor'

import { getPixelCreationInfoQueryKey } from '../api/queryKey'
import { WithResponse } from '../models/Response'
import {
  createPixelConversion,
  editPixelConversion,
  getDurationOptions,
  getPixelConversionAdvancedSettings,
  getPixelConversionAttributionTypes,
  getPixelConversionPiggybackTypes,
  sendEmail,
} from '../api/pixel'
import { queryClient } from '../cache/query'
import { ConversionCount, PixelConversionInfo } from '../models/Conversion'

type useListFilterProps = Pick<UseQueryOptions, 'enabled' | 'onSuccess'>

export const usePixelAdvancedSettingList = (props?: useListFilterProps) => {
  const data = useQuery(
    getPixelCreationInfoQueryKey.keys({
      scope: 'getPixelConversionAdvancedSettings',
    }),
    getPixelConversionAdvancedSettings,
    {
      enabled: props?.enabled,
      onError: (e: WithResponse) => {
        enqueueSnackbar(
          e.errorObjects
            ? (e.errorObjects[0]?.error as string)
            : 'Something went wrong!',
          {
            variant: 'error',
          }
        )
      },
      keepPreviousData: true,
    }
  )
  return data
}

export const usePixelPiggybackTypeList = (props?: useListFilterProps) => {
  const data = useQuery(
    getPixelCreationInfoQueryKey.keys({
      scope: 'getPixelConversionPiggybackTypes',
    }),
    getPixelConversionPiggybackTypes,
    {
      enabled: props?.enabled,
      onError: (e: WithResponse) => {
        enqueueSnackbar(
          e.errorObjects
            ? (e.errorObjects[0]?.error as string)
            : 'Something went wrong!',
          {
            variant: 'error',
          }
        )
      },
      keepPreviousData: true,
    }
  )
  return data.data?.data
}

export const usePixelConversionAttributionList = (
  props?: useListFilterProps
) => {
  const data = useQuery(
    getPixelCreationInfoQueryKey.keys({
      scope: 'getPixelConversionAttributionTypes',
    }),
    getPixelConversionAttributionTypes,
    {
      enabled: props?.enabled,
      onError: (e: WithResponse) => {
        enqueueSnackbar(
          e.errorObjects
            ? (e.errorObjects[0]?.error as string)
            : 'Something went wrong!',
          {
            variant: 'error',
          }
        )
      },
      keepPreviousData: true,
    }
  )
  return data.data?.data
}

export const useDurationOptions = (props?: useListFilterProps) => {
  const data = useQuery(
    getPixelCreationInfoQueryKey.keys({
      scope: 'getDurationOptions',
    }),
    getDurationOptions,
    {
      enabled: props?.enabled,
      onError: (e: WithResponse) => {
        enqueueSnackbar(
          e.errorObjects
            ? (e.errorObjects[0]?.error as string)
            : 'Something went wrong!',
          {
            variant: 'error',
          }
        )
      },
      keepPreviousData: true,
    }
  )
  return data
}

const typeMapper = {
  all: '',
  pixel: '1',
}

export const useCreatePixel = (
  conversionType: string,
  successCallback: (data: PixelConversionInfo | undefined) => void
) => {
  const mutationResult = useMutation(createPixelConversion, {
    mutationKey: ['createPixel'],
    onSuccess: data => {
      successCallback(data)
      queryClient.refetchQueries({
        predicate: query => {
          return (
            //@ts-ignore
            query.queryKey?.[0]?.scope === 'getConversionList' &&
            //@ts-ignore
            query.queryKey?.[0]?.typeIds === typeMapper[conversionType]
          )
        },
      })
      queryClient.setQueriesData(
        {
          predicate: (query: any) =>
            query.queryKey?.[0]?.scope === 'getConversionsCount',
        },
        (conversionCount: ConversionCount | undefined) => ({
          PIXEL: (conversionCount?.PIXEL ?? 0) + 1,
          POSTBACK: conversionCount?.POSTBACK ?? 0,
          ALL: (conversionCount?.ALL ?? 0) + 1,
        })
      )
    },
    onError: (e: any) => {
      enqueueSnackbar(
        e.errorObjects
          ? (e.errorObjects[0]?.error as string)
          : 'Something went wrong. Please try after sometime.',
        {
          variant: 'error',
        }
      )
    },
  })

  return mutationResult
}

export const useEditPixel = (conversionType: string) => {
  const mutationResult = useMutation(editPixelConversion, {
    mutationKey: ['editPixel'],
    onSuccess: () => {
      queryClient.refetchQueries({
        predicate: query => {
          return (
            //@ts-ignore
            query.queryKey?.[0]?.scope === 'getConversionList' &&
            //@ts-ignore
            query.queryKey?.[0]?.typeIds === typeMapper[conversionType]
          )
        },
      })
    },
    onError: (e: any) => {
      enqueueSnackbar(
        e.errorObjects
          ? (e.errorObjects[0]?.error as string)
          : 'Something went wrong. Please try after sometime.',
        {
          variant: 'error',
        }
      )
    },
  })
  return mutationResult
}

export const useSendEmail = () => {
  const mutationResult = useMutation(sendEmail, {
    mutationKey: ['editPixel'],
    onSuccess: res => {
      enqueueSnackbar(res as string, {
        variant: 'success',
      })
    },
    onError: (e: any) => {
      enqueueSnackbar(
        e.errorObjects
          ? (e.errorObjects[0]?.error as string)
          : 'Something went wrong. Please try after sometime.',
        {
          variant: 'error',
        }
      )
    },
  })
  return mutationResult
}
